import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Mail, Phone, Loader2, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  created_at: string;
  is_read: boolean;
}

const AdminContacts = () => {
  const queryClient = useQueryClient();
  const [selectedContact, setSelectedContact] = useState<ContactSubmission | null>(null);

  const { data: contacts, isLoading } = useQuery({
    queryKey: ["admin-contacts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_submissions")
        .select("*")
        .order("is_read", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ContactSubmission[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("contact_submissions")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["admin-contacts-count"] });
      toast.success("Contact submission deleted");
      setSelectedContact(null);
    },
    onError: (error) => {
      toast.error("Failed to delete: " + error.message);
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("contact_submissions")
        .update({ is_read: true } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["admin-contacts-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const unreadIds = contacts?.filter(c => !c.is_read).map(c => c.id) || [];
      if (unreadIds.length === 0) return;
      const { error } = await supabase
        .from("contact_submissions")
        .update({ is_read: true } as any)
        .in("id", unreadIds);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["admin-contacts-count"] });
      toast.success("All messages marked as read");
    },
    onError: (error) => {
      toast.error("Failed to mark all as read: " + error.message);
    },
  });

  const handleSelectContact = (contact: ContactSubmission) => {
    setSelectedContact(contact);
    if (!contact.is_read) {
      markReadMutation.mutate(contact.id);
    }
  };

  const unreadCount = contacts?.filter(c => !c.is_read).length || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-serif">Contact Submissions</h1>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all as read
            </Button>
          )}
          {unreadCount > 0 && (
            <span className="bg-primary text-primary-foreground text-xs font-bold rounded-full px-2.5 py-1">
              {unreadCount} unread
            </span>
          )}
          <span className="text-muted-foreground">{contacts?.length || 0} messages</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {contacts && contacts.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow
                      key={contact.id}
                      className={`cursor-pointer ${selectedContact?.id === contact.id ? "bg-muted" : ""} ${!contact.is_read ? "font-semibold" : "opacity-70"}`}
                      onClick={() => handleSelectContact(contact)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {!contact.is_read && (
                            <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                          )}
                          <span className={!contact.is_read ? "font-semibold" : ""}>{contact.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className={!contact.is_read ? "font-semibold" : ""}>{contact.subject || "No subject"}</TableCell>
                      <TableCell>{format(new Date(contact.created_at), "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMutation.mutate(contact.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-center py-8">No contact submissions yet</p>
            )}
          </CardContent>
        </Card>

        {/* Contact Detail */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Message Detail
              {selectedContact?.is_read && (
                <CheckCheck className="w-4 h-4 text-muted-foreground" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedContact ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">From</p>
                  <p className="font-medium">{selectedContact.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">
                    {selectedContact.email}
                  </a>
                </div>
                {selectedContact.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${selectedContact.phone}`} className="text-primary hover:underline">
                      {selectedContact.phone}
                    </a>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Subject</p>
                  <p>{selectedContact.subject || "No subject"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Message</p>
                  <p className="whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Received</p>
                  <p>{format(new Date(selectedContact.created_at), "PPpp")}</p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Select a message to view details</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminContacts;